import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'

import { History } from 'history'

import PagedList from 'core/application/commons/pagedList'
import getEmployees, { EmployeeResponse, EmployeesFilter } from 'core/application/companies/getEmployees'

import { Card, Icon, Table } from 'common/components/'
import Avatar from 'common/components/dataDisplay/Avatar'

import useQueryRequest from 'common/hooks/useQueryRequest'

import { Button, Dropdown, Menu, MenuItem } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCurrentProduct } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

//
import RehireEmployeeModal from '../rehireEmployeeModal'

//

type TerminatedEmployeesTableProps = {
  reload?: boolean
  onLoaded?: () => void
  onRehireClick: () => void
  search?: string
}

const TerminatedEmployeesTable: React.FC<TerminatedEmployeesTableProps> = (props) => {
  const { t: translate } = useTranslation()
  const product = useCurrentProduct()
  const history = useHistory()
  const [pagedEmployees, isEmployeesLoading, sendGetEmployeesQuery] = useQueryRequest<PagedList<EmployeeResponse>>(null)

  const [filter, setFilter] = useState<EmployeesFilter>({
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'createdAt',
    orderDescending: true,
    eligible: false,
    onlyTerminated: true,
  })
  const [isEmploymentDetailsModalOpen, setEmploymentDetailsModalOpen] = useState(false)
  const [selectedCompanyUserId, setSelectedCompanyUserId] = useState('')

  const refreshTable = async () => {
    await sendGetEmployeesQuery(() => getEmployees(filter))
    if (props.onLoaded) {
      props.onLoaded()
    }
  }

  const handleModal = (companyUserId: string) => {
    setEmploymentDetailsModalOpen(true)
    setSelectedCompanyUserId(companyUserId)
  }

  const handleCloseModal = () => {
    setEmploymentDetailsModalOpen(false)
  }

  useEffect(() => {
    ;(async () => {
      await refreshTable()
    })()
  }, [filter])

  useEffect(() => {
    if (props.search != undefined) {
      setFilter({
        ...filter,
        searchValue: props.search,
      })
    }
  }, [props.search])

  useEffect(() => {
    if (props.reload) {
      ;(async () => {
        await refreshTable()
      })()
    }
  }, [props.reload])

  const menu = (record: EmployeeResponse, history: History<unknown>): JSX.Element => {
    return (
      <Menu>
        <MenuItem key={1}>
          <Button type="text" icon="fa fa-rotate-right" onClick={() => handleModal(record.id)}>
            {translate('admin-dashboard.manage-people.employee.rehire-button')}
          </Button>
        </MenuItem>
        <MenuItem
          key={2}
          onClick={() => history.push(`${ROUTE_PATHS.MANAGE_PEOPLE}/edit-personal-details/${record.id}`)}
        >
          <div className="flex ai-center p-4">
            <Icon type="fas fa-pencil" className="mr-8" />
            Edit
          </div>
        </MenuItem>
      </Menu>
    )
  }

  const getColumns = (productId: number): any[] => {
    const columns: any[] = [
      {
        title: translate('admin-dashboard.manage-people.employee.first-name-label'),
        dataIndex: 'firstName',
        key: 'firstName',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        className: 't-terminated-employees-firstName',
        render: (text: string, record: EmployeeResponse) => (
          <span>
            <Link to={`${ROUTE_PATHS.MANAGE_PEOPLE}/view/${record.id}`} className="fullname_link">
              <div className="flex align-center" style={{ gap: '10px' }}>
                <Avatar style={{ backgroundColor: '#11D175' }}>{record.firstName[0]}</Avatar>
                {record.firstName}
              </div>
            </Link>
          </span>
        ),
      },
      {
        title: translate('admin-dashboard.manage-people.employee.last-name-label'),
        dataIndex: 'lastName',
        key: 'lastName',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        className: 't-terminated-employees-lastName',
        render: (text: string, record: EmployeeResponse) => <span>{record.lastName}</span>,
      },
      {
        title: translate('admin-dashboard.manage-people.employee.work-email'),
        dataIndex: 'workEmail',
        key: 'workEmail',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        className: 't-terminated-employees-workEmail',
      },
      {
        title: translate('admin-dashboard.manage-people.employee.employment-status'),
        dataIndex: 'employmentStatus',
        key: 'employmentStatus',
        className: 't-terminated-employees-employmentStatus',
      },
      {
        title: translate('admin-dashboard.manage-people.employee.class'),
        dataIndex: 'employeeClass',
        key: 'employeeClass',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        className: 't-terminated-employees-employeeClass',
      },
      {
        title: translate('admin-dashboard.manage-people.employee.pk-status'),
        dataIndex: 'status',
        key: 'status',
        className: 't-terminated-employees-status',
      },
      {
        title: translate('admin-dashboard.manage-people.employee.termination-date'),
        dataIndex: 'terminationDate',
        key: 'terminationDate',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        className: 't-terminated-employees-terminationDate',
        render: (terminationDate: Date) => (terminationDate ? parseZoneFormat(terminationDate) : null),
      },
    ]
    // if (productId == ProductIds.Perks) {
    let actionCol = {
      title: translate('admin-dashboard.manage-people.employee.action'),
      key: 'action',
      className: 't-terminated-employees-action',
      render: (text: string, record: EmployeeResponse) => (
        <>
          <Dropdown overlay={menu(record, history)}>
            <Button>...</Button>
          </Dropdown>
        </>
      ),
    }
    columns.push(actionCol)
    //}
    return columns
  }
  const columnToSort: any = getColumns(product.productId).find((c) => c.key === filter.orderColumn)
  if (columnToSort) {
    columnToSort.defaultSortOrder = filter.orderDescending ? 'descend' : 'ascend'
  }

  const onTableFilterChanged = (pagination: any, filters: any, sorter: any) => {
    setFilter({
      ...filter,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'createdAt',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  return (
    <Card
      className="mb-16"
      title={<h5 className="mb-0">{translate('admin-dashboard.manage-people.view-people.qsehra-title-terminated')}</h5>}
    >
      <Table
        className={'t-terminated-employees'}
        dataSource={pagedEmployees?.items}
        onChange={onTableFilterChanged}
        loading={isEmployeesLoading}
        columns={getColumns(product.productId)}
        scroll={{ x: 1000 }}
        pagination={{
          total: pagedEmployees?.totalItemCount,
          pageSize: pagedEmployees?.pageSize,
          current: pagedEmployees?.pageNumber,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '30', '40'],
        }}
      />{' '}
      {isEmploymentDetailsModalOpen && selectedCompanyUserId && (
        <RehireEmployeeModal
          isOpen={isEmploymentDetailsModalOpen}
          onClose={handleCloseModal}
          onSubmit={async () => {
            await refreshTable()
            props.onRehireClick()
          }}
          companyUserId={selectedCompanyUserId}
        />
      )}
    </Card>
  )
}

export default TerminatedEmployeesTable
